import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { state } from './state.js';

/* ═══════════════════════════════════════════
   § 1. THREE.JS ENGINE SETUP
   § 2. STUDIO LIGHTING
   Returns a context object that main.js passes
   to every module that needs Three.js objects.
   ═══════════════════════════════════════════ */

export function initScene() {
  const container = document.getElementById('canvas-container');

  // ── Scene ──────────────────────────────────
  const scene = new THREE.Scene();
  scene.background = null;

  // ── Camera ─────────────────────────────────
  const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000);
  camera.position.set(0, 150, 250);

  // Audio Context & Listener
  const audioListener = new THREE.AudioListener();
  camera.add(audioListener);

  // ── Renderer ───────────────────────────────
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  // DRS: Start at NATIVE quality — only drop if device can't handle it after warm-up
  renderer.setPixelRatio(state._nativeDPR);
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Premium soft shadows
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  container.appendChild(renderer.domElement);

  // ── iOS Safari gesture interceptors ────────
  // Safari fires proprietary gesture* events that trigger native pinch-zoom
  // even when CSS touch-action:none is set. Intercept them at the JS level
  // so all multi-touch input routes exclusively through OrbitControls.
  const _canvas = renderer.domElement;
  ['gesturestart', 'gesturechange', 'gestureend'].forEach(evt => {
    _canvas.addEventListener(evt, e => e.preventDefault(), { passive: false });
  });
  // Prevent long-press context menu on mobile (breaks touch flow)
  _canvas.addEventListener('contextmenu', e => e.preventDefault());

  // ── Orbit Controls ─────────────────────────
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.maxPolarAngle = Math.PI / 2.1;
  controls.minDistance = 20;
  controls.maxDistance = 1500;
  // Swap: Left-click = Pan, Right-click = Orbit (map-style navigation)
  controls.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE };
  // Mobile map-style controls: 1-finger pan, 2-finger rotate and dolly
  controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_ROTATE };
  controls.screenSpacePanning = true;

  // ── Clock ──────────────────────────────────
  const clock = new THREE.Clock();

  /* ═══════════════════════════════════════════
     § 2. STUDIO LIGHTING
     ═══════════════════════════════════════════ */
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.7); // Soft fill
  scene.add(ambientLight);

  const dirLight = new THREE.DirectionalLight(0xfffff0, 1.5); // Sun
  dirLight.position.set(100, 250, 100);
  dirLight.castShadow = true;
  dirLight.shadow.mapSize.width = 2048;
  dirLight.shadow.mapSize.height = 2048;
  dirLight.shadow.camera.near = 0.5;
  dirLight.shadow.camera.far = 1000;
  const d = 300;
  dirLight.shadow.camera.left = -d;
  dirLight.shadow.camera.right = d;
  dirLight.shadow.camera.top = d;
  dirLight.shadow.camera.bottom = -d;
  dirLight.shadow.bias = -0.0005;
  scene.add(dirLight);

  // ── Populate state with THREE.js object slots ──
  // Camera positions (set to defaults; asset-loader.js overwrites after GLB load)
  state.baseCenter = new THREE.Vector3();
  state.default3DPos = new THREE.Vector3(0, 150, 250);
  state.defaultTopPos = new THREE.Vector3(0, 300, 0.1);

  // Camera animation objects (camera-controls.js uses these)
  state.startCamPos = new THREE.Vector3();
  state.startCtrlTarget = new THREE.Vector3();
  state.startCamQuat = new THREE.Quaternion();
  state.endCamQuat = new THREE.Quaternion();

  return { scene, camera, renderer, controls, clock, audioListener };
}
